import React, { useEffect, useMemo, useState } from 'react'
import PqrSectionModal from './PqrSectionModal'
import { planPqrDefaults } from '../../../utils/pqrRevisionPlanner'
import { formatPqrDate } from '../../../utils/formatPqrDateTime'
import type { ReportListItem } from '../../../utils/reportsClient'

/**
 * Minimal weld descriptor for the picker. Mirrors the subset of
 * `floorsheet.welds[i]` that the picker needs to identify a weld; we keep
 * this tight to avoid pulling the entire FloorSheet type tree into the
 * picker's call-sites.
 */
export interface PqrWeldOption {
  /**
   * `floorsheet.welds[i].weldId` — the user-facing weld identifier (e.g.
   * `"A"`, `"W-01"`) used as the URL query / `weldparams.${weldId}` key.
   * Empty when the operator hasn't filled the field; such welds are
   * filtered out by `readWeldOptionsFromSections` since they can't anchor
   * a per-weld scope.
   */
  weldId: string
  /** `weldNumber` from the row (user-typed numeric label). May be blank. */
  weldNumber: string
}

/**
 * Extract pickable welds from the sections blob. Drops welds without a
 * non-blank `weldId` because a blank id has no scope target. Sort order:
 * `weldNumber` lexicographic (stable for users who number welds 1..N).
 */
export function readWeldOptionsFromSections(
  sections: Record<string, unknown> | null | undefined
): PqrWeldOption[] {
  if (!sections) return []
  const floorsheet = sections.floorsheet as { welds?: unknown } | undefined
  const welds = Array.isArray(floorsheet?.welds) ? (floorsheet!.welds as unknown[]) : []
  const out: PqrWeldOption[] = []
  for (const raw of welds) {
    if (!raw || typeof raw !== 'object') continue
    const w = raw as { weldId?: unknown; weldNumber?: unknown }
    const weldId = typeof w.weldId === 'string' ? w.weldId.trim() : ''
    if (!weldId) continue
    const weldNumber = typeof w.weldNumber === 'string' ? w.weldNumber.trim() : ''
    out.push({ weldId, weldNumber })
  }
  return out.sort((a, b) => a.weldNumber.localeCompare(b.weldNumber, undefined, { numeric: true }))
}

// Polish-pass slice 06: hint dates render through the shared UTC
// formatter so the picker text matches the rest of the PQR module
// (`04 Jun 2026`) regardless of viewer locale.
const formatHintDate = formatPqrDate

interface Props {
  open: boolean
  /**
   * Available welds, pre-extracted by the caller (so the modal stays pure
   * about data fetching). Empty array = "no welds available"; the modal
   * surfaces a clear empty-state.
   */
  welds: PqrWeldOption[]
  /**
   * Surviving PQR reports for the pipe set (pre-fetched by the caller).
   * Drives the planner-derived default PQR No. / Rev. and the per-weld
   * "Last generated:" hints. Added in `pqr-report-history-and-rev`
   * slice 03.
   */
  reports: ReportListItem[]
  /**
   * Currently-selected `weldId` (legacy single-select convenience).
   * Treated as `initialWeldIds: [initialWeldId]` when `initialWeldIds`
   * isn't supplied. Falsy = nothing pre-selected.
   */
  initialWeldId?: string | null
  /**
   * Currently-selected weld IDs (multi-select). Wins over
   * `initialWeldId` when both are supplied. Order preserved as the
   * user's pick order on the picker.
   */
  initialWeldIds?: string[] | null
  /**
   * Apply with the picked weld(s) plus the (possibly user-overridden)
   * PQR No. and Rev. `weldIds` is non-empty (at least one weld picked).
   * `weldId` is `weldIds[0]` for legacy callers that still read a
   * single weld; new callers should rely on `weldIds`.
   */
  onApply: (payload: {
    weldId: string
    weldIds: string[]
    pqrNumber: string
    revision: string
  }) => void
  onCancel: () => void
}

/**
 * "Select Weld for PQR" modal. Surfaced from PqrDetails > "Generate PQR
 * Report" — the user picks a weld AND confirms / overrides the PQR No.
 * and Rev. before landing on the editable preview. The picked values
 * are appended to the preview URL
 * (`?new=1&weldId=<id>&pqrNumber=<n>&revision=<r>`).
 *
 * Display rule (per user direction): show "Weld Number + Weld ID only",
 * with an optional one-line hint underneath welds that have prior PQRs.
 */
export default function PqrWeldPickerModal({
  open,
  welds,
  reports,
  initialWeldId,
  initialWeldIds,
  onApply,
  onCancel
}: Props) {
  // Multi-weld selection state. `selected` preserves user pick order so
  // the first picked weld drives the back-compat single-valued fields
  // and stays first in any stacked render.
  const seedSelection = (): string[] => {
    if (initialWeldIds && initialWeldIds.length > 0) {
      return initialWeldIds.map((s) => s.trim()).filter(Boolean)
    }
    const single = initialWeldId?.trim()
    return single ? [single] : []
  }
  const [selected, setSelected] = useState<string[]>(seedSelection)
  const [pqrNumber, setPqrNumber] = useState<string>('')
  const [revision, setRevision] = useState<string>('')
  const [suggestedPqrNumber, setSuggestedPqrNumber] = useState<string>('')
  const [suggestedRevision, setSuggestedRevision] = useState<string>('')

  // Reset the working selection whenever the modal opens fresh.
  useEffect(() => {
    if (open) {
      setSelected(seedSelection())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialWeldId, initialWeldIds])

  // Re-seed the PQR No. / Rev. inputs whenever the picked weld set
  // changes. The planner is keyed by the sorted weld-set so single-
  // and multi-weld trains get distinct PQR No. proposals.
  const selectedKey = useMemo(() => [...selected].sort().join('|'), [selected])
  useEffect(() => {
    if (selected.length === 0) {
      setPqrNumber('')
      setRevision('')
      setSuggestedPqrNumber('')
      setSuggestedRevision('')
      return
    }
    const planned = planPqrDefaults({ reports, weldIds: selected })
    setPqrNumber(planned.defaultPqrNumber)
    setRevision(planned.defaultRevision)
    setSuggestedPqrNumber(planned.defaultPqrNumber)
    setSuggestedRevision(planned.defaultRevision)
  }, [selectedKey, reports])

  // Per-row "Last generated:" hint. For multi-select the hint still
  // reflects each weld's own most-recent prior (independent of the
  // current multi-pick), so users can see what's been done to each
  // weld individually.
  const hintsByWeld = useMemo(() => {
    const map = new Map<string, ReturnType<typeof planPqrDefaults>['lastGeneratedHint']>()
    for (const w of welds) {
      map.set(w.weldId, planPqrDefaults({ reports, weldIds: [w.weldId] }).lastGeneratedHint)
    }
    return map
  }, [welds, reports])

  const isEmpty = welds.length === 0
  const trimmedPqrNumber = pqrNumber.trim()
  const trimmedRevision = revision.trim()
  const applyDisabledReason = selected.length === 0
    ? 'Pick at least one weld to continue'
    : !trimmedPqrNumber
      ? 'Enter a PQR No. to continue'
      : !trimmedRevision
        ? 'Enter a Rev. to continue'
        : undefined

  const toggleSelected = (weldId: string) => {
    setSelected((prev) =>
      prev.includes(weldId) ? prev.filter((w) => w !== weldId) : [...prev, weldId]
    )
  }

  const handleApply = () => {
    if (selected.length === 0 || !trimmedPqrNumber || !trimmedRevision) return
    onApply({
      weldId: selected[0],
      weldIds: selected,
      pqrNumber: trimmedPqrNumber,
      revision: trimmedRevision
    })
  }

  /**
   * Implements the "edit PQR No. away from suggestion → reset Rev. to 0,
   * edit it back → restore planner Rev." rule from the issue. The reset
   * is intentionally synchronous (same render) so the user sees the new
   * Rev. immediately as they type.
   */
  const handlePqrNumberChange = (next: string) => {
    setPqrNumber(next)
    if (next === suggestedPqrNumber) {
      setRevision(suggestedRevision)
    } else {
      setRevision('0')
    }
  }

  const idListKey = useMemo(() => welds.map((w) => w.weldId).join('|'), [welds])

  return (
    <PqrSectionModal
      open={open}
      title="Select welds for PQR"
      isModalDirty={selectedKey !== [...seedSelection()].sort().join('|')}
      applyLabel="Continue"
      applyDisabledReason={applyDisabledReason}
      panelMaxWidthClassName="max-w-2xl"
      onApply={handleApply}
      onCancel={onCancel}
    >
      <div className="flex flex-col gap-4">
        {isEmpty ? (
          <p className="rounded-md border border-hero-red-30 bg-hero-red-10 px-3 py-2 text-sm text-hero-red-90">
            No welds with a weld ID are recorded on the FloorSheet yet. Return to the
            FloorSheet to assign at least one weld id before generating a PQR.
          </p>
        ) : (
          <fieldset
            className="flex flex-col gap-1"
            // `key` swaps reset internal scroll position when welds change.
            key={idListKey}
            data-testid="pqr-weld-picker-list"
          >
            <legend className="sr-only">Welds</legend>
            <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white">
              {welds.map((w) => {
                const checked = selected.includes(w.weldId)
                const hint = hintsByWeld.get(w.weldId)
                return (
                  <label
                    key={w.weldId}
                    className={`flex cursor-pointer flex-col gap-1 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 hover:bg-gray-50 ${
                      checked ? 'bg-brand-red-50/40' : ''
                    }`}
                    data-testid={`pqr-weld-option-${w.weldId}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        name="pqr-weld-pick"
                        className="h-4 w-4 rounded text-brand-red-600 focus:ring-brand-red-500"
                        value={w.weldId}
                        checked={checked}
                        onChange={() => toggleSelected(w.weldId)}
                      />
                      {/* Display rule (per user direction): "Weld Number + Weld ID only". */}
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium text-gray-800">
                          {w.weldNumber || '\u2014'}
                        </span>
                        <span className="ml-2 text-gray-500">{w.weldId}</span>
                      </span>
                    </div>
                    {hint && (
                      <span className="ml-7 text-[11px] text-gray-500">
                        Last generated: {hint.pqrNumber} Rev {hint.revision}
                        {formatHintDate(hint.uploadedAt) &&
                          ` on ${formatHintDate(hint.uploadedAt)}`}
                        {hint.generatedByEmail && ` by ${hint.generatedByEmail}`}
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
          </fieldset>
        )}

        {selected.length > 0 && (
          <div className="grid grid-cols-2 gap-3" data-testid="pqr-picker-identifiers">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
              PQR No.
              <input
                type="text"
                value={pqrNumber}
                onChange={(e) => handlePqrNumberChange(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
                data-testid="pqr-picker-pqrNumber"
                autoComplete="off"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
              Rev.
              <input
                type="text"
                value={revision}
                onChange={(e) => setRevision(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
                data-testid="pqr-picker-revision"
                autoComplete="off"
              />
            </label>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Each selected weld stacks its own Materials (pup 1 / pup 2),
          Joint Preparation, Pass Sequence and Welder rows on the
          generated PQR. Welding Parameters min/max aggregate across
          all picked welds.
        </p>
        <p
          data-testid="pqr-picker-utc-caption"
          className="text-[11px] italic text-gray-500"
        >
          Times shown in UTC
        </p>
      </div>
    </PqrSectionModal>
  )
}
